import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Search, ArrowLeft, Trash2, User, Mail, Phone, Calendar, ChevronRight, ExternalLink } from "lucide-react";
import HexBackground from "@/components/HexBackground";
import { api, setAuthToken } from "@/lib/api";

function LoginView({ onLogged }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post("/admin/login", { username, password });
      setAuthToken(res.data.token);
      toast.success("Вход выполнен");
      onLogged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Ошибка входа");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto py-16 px-4 relative z-10">
      <div className="form-card p-8">
        <button
          data-testid="admin-back-to-form"
          className="text-sm mb-4 inline-flex items-center gap-1"
          style={{ color: "#7a1d2e" }}
          onClick={() => navigate("/")}
        >
          <ArrowLeft size={14} /> На главную
        </button>
        <h1 className="font-heading text-2xl font-bold mb-1">Вход администратора</h1>
        <p className="text-sm mb-6" style={{ color: "#6b7280" }}>Только для организаторов</p>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="field-label">Логин</label>
            <input
              data-testid="admin-username-input"
              className="text-input"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="admin"
              autoFocus
            />
          </div>
          <div>
            <label className="field-label">Пароль</label>
            <input
              data-testid="admin-password-input"
              className="text-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
            />
          </div>
          <button
            data-testid="admin-login-submit-button"
            type="submit"
            disabled={loading}
            className="btn-primary w-full"
            style={{ width: "100%" }}
          >
            {loading ? "Входим..." : "Войти"}
          </button>
        </form>
      </div>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("ru-RU", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}

function ApplicantDetail({ id, onBack, schema, onDeleted }) {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/admin/applications/${id}`);
        setDoc(res.data);
      } catch (e) {
        toast.error("Не удалось загрузить заявку");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const del = async () => {
    if (!window.confirm("Удалить заявку безвозвратно?")) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/applications/${id}`);
      toast.success("Заявка удалена");
      onDeleted();
    } catch (e) {
      toast.error("Ошибка удаления");
    } finally {
      setDeleting(false);
    }
  };

  if (loading) return <div className="form-card p-8 text-center">Загрузка...</div>;
  if (!doc) return null;

  const renderVal = (key) => {
    if (key === "is_budget") return doc[key] ? "Да" : "Нет";
    if (key === "submitted_at") return formatDate(doc[key]);
    return doc[key] || "—";
  };

  return (
    <div className="form-card p-6 sm:p-10" data-testid="admin-applicant-detail">
      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
        <button
          data-testid="admin-detail-back"
          className="text-sm inline-flex items-center gap-1"
          style={{ color: "#7a1d2e" }}
          onClick={onBack}
        >
          <ArrowLeft size={14} /> К списку
        </button>
        <button
          data-testid="admin-detail-delete"
          onClick={del}
          disabled={deleting}
          className="text-sm inline-flex items-center gap-1"
          style={{ color: "#dc2626" }}
        >
          <Trash2 size={14} /> {deleting ? "Удаление..." : "Удалить"}
        </button>
      </div>

      <h1 className="font-heading text-2xl sm:text-3xl font-bold mb-1">{doc.fio}</h1>
      <div className="text-sm mb-6" style={{ color: "#6b7280" }}>
        Отправлено: {formatDate(doc.submitted_at)} · {doc.synced_to_sheets ? "✓ В Google Sheets" : "⚠ Не синхронизировано"}
      </div>

      {schema.map(({ key, label }) => {
        if (key === "submitted_at") return null;
        const value = renderVal(key);
        return (
          <div key={key} className="mb-4">
            <div className="section-title">{label}</div>
            {(key === "drive_link" || key === "vk_link" || key === "telegram_link") && doc[key] ? (
              <a
                href={doc[key]}
                target="_blank"
                rel="noreferrer"
                className="readonly-block"
                style={{ display: "block", color: "#7a1d2e", textDecoration: "underline" }}
              >
                {doc[key]} <ExternalLink size={12} style={{ display: "inline" }} />
              </a>
            ) : (
              <div className="readonly-block">{value}</div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function DashboardView({ onLogout }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [schema, setSchema] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [listRes, schemaRes] = await Promise.all([
        api.get("/admin/applications"),
        api.get("/admin/schema"),
      ]);
      setItems(listRes.data.items || []);
      setSchema(schemaRes.data.fields || []);
    } catch (e) {
      if (e?.response?.status === 401) {
        toast.error("Сессия истекла — войдите заново");
        onLogout();
      } else {
        toast.error("Ошибка загрузки");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) =>
      [it.fio, it.email, it.phone, it.course].some((v) => (v || "").toLowerCase().includes(q))
    );
  }, [items, query]);

  if (selectedId) {
    return (
      <div className="max-w-3xl mx-auto py-8 px-4 relative z-10">
        <ApplicantDetail
          id={selectedId}
          schema={schema}
          onBack={() => setSelectedId(null)}
          onDeleted={() => { setSelectedId(null); load(); }}
        />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto py-8 px-4 relative z-10">
      <div className="form-card p-6 sm:p-8">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold">Заявки участников</h1>
            <p className="text-sm" style={{ color: "#6b7280" }}>
              Всего: <span className="font-semibold">{items.length}</span>
            </p>
          </div>
          <button
            data-testid="admin-logout-button"
            className="btn-secondary inline-flex items-center gap-1"
            onClick={onLogout}
          >
            <LogOut size={14} /> Выйти
          </button>
        </div>

        <div className="relative mb-6">
          <Search size={16} style={{ position: "absolute", left: 12, top: 13, color: "#6b7280" }} />
          <input
            data-testid="admin-search-input"
            className="text-input"
            style={{ paddingLeft: 38 }}
            placeholder="Поиск по ФИО, email, телефону, курсу..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>

        {loading ? (
          <div className="text-center py-10" style={{ color: "#6b7280" }}>Загрузка...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-10" style={{ color: "#6b7280" }}>
            {items.length === 0 ? "Пока нет ни одной заявки" : "Ничего не найдено"}
          </div>
        ) : (
          <div className="grid gap-3">
            {filtered.map((it) => (
              <div
                key={it.id}
                data-testid="admin-applicant-card"
                className="admin-card"
                onClick={() => setSelectedId(it.id)}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <User size={16} className="text-burgundy" />
                      <div className="font-semibold truncate">{it.fio}</div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-1 gap-x-4 text-xs" style={{ color: "#4b5563" }}>
                      <div>Курс: <span className="font-medium">{it.course || "—"}</span></div>
                      <div className="flex items-center gap-1"><Calendar size={12} /> {formatDate(it.submitted_at)}</div>
                      <div className="flex items-center gap-1 truncate"><Mail size={12} /> {it.email}</div>
                      <div className="flex items-center gap-1"><Phone size={12} /> {it.phone}</div>
                    </div>
                  </div>
                  <ChevronRight size={20} className="text-burgundy flex-shrink-0" />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminPage() {
  const [logged, setLogged] = useState(!!localStorage.getItem("admin_token"));

  const logout = () => {
    setAuthToken(null);
    setLogged(false);
  };

  return (
    <div className="page-bg">
      <HexBackground />
      {logged ? (
        <DashboardView onLogout={logout} />
      ) : (
        <LoginView onLogged={() => setLogged(true)} />
      )}
    </div>
  );
}

